// Kalenderinvitation (.ics, RFC 5545/5546) til kundens bekræftelse – samme kode i testversionen og i drift (send-mail).
//  • Én VEVENT pr. booking med et fast UID (booking-<id>@<klient>.freemi.dk), så kundens kalender opdaterer den samme aftale,
//    når tiden flyttes (METHOD:REQUEST med højere SEQUENCE) og fjerner den, når bookingen aflyses (METHOD:CANCEL).
//  • Flere biler i samme bestilling: flere VEVENT'er i én fil.
//  • Tider skrives i UTC (…Z), så alle kalendere viser dansk tid rigtigt – også over sommertid.
import { lokalTilMs, type Handling, type Kontekst, type Workflow } from './automation.ts';

export type KalenderMetode = 'REQUEST' | 'CANCEL';
export type KalenderAftale = { booking_id: number; dato: string; start: number; slut: number; titel: string; adresse: string; beskrivelse?: string };
export type KalenderInd = {
  metode: KalenderMetode; klient: string; firma: string; arrangoer: string; deltager?: string;
  aftaler: KalenderAftale[]; nu: number;
};

/** Fast UID for en booking – samme ved oprettelse, flytning og aflysning. */
export const kalenderUid = (klient: string, booking_id: number) => `booking-${booking_id}@${(klient || 'freemi').toLowerCase().replace(/[^a-z0-9-]/g, '')}.freemi.dk`;

/** Stiger for hver ny version af aftalen (sekunder siden 2026), så kalenderen altid tager den nyeste. */
export const kalenderSekvens = (nu: number) => Math.max(0, Math.floor((nu - Date.UTC(2026, 0, 1)) / 1000));

const utc = (ms: number) => new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
/** Tekst i en egenskab: \ ; , og linjeskift skal escapes. */
export const icsTekst = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

/** Linjer over 75 oktetter foldes (CRLF + mellemrum) uden at dele et tegn. */
export function foldLinje(l: string): string {
  const enc = new TextEncoder();
  if (enc.encode(l).length <= 75) return l;
  const dele: string[] = []; let nu = ''; let n = 0;
  for (const tegn of l) {
    const b = enc.encode(tegn).length;
    if (n + b > (dele.length ? 74 : 75)) { dele.push(nu); nu = ''; n = 0; }
    nu += tegn; n += b;
  }
  dele.push(nu);
  return dele.join('\r\n ');
}

/** Hele .ics-filen (CRLF-linjer). */
export function ics(i: KalenderInd): string {
  const sek = kalenderSekvens(i.nu);
  const navn = icsTekst(i.firma).replace(/"/g, '');
  const l = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Freemi//Booking//DA', 'CALSCALE:GREGORIAN', `METHOD:${i.metode}`];
  for (const a of i.aftaler) {
    l.push('BEGIN:VEVENT', `UID:${kalenderUid(i.klient, a.booking_id)}`, `SEQUENCE:${sek}`, `DTSTAMP:${utc(i.nu)}`,
      `DTSTART:${utc(lokalTilMs(a.dato, a.start))}`, `DTEND:${utc(lokalTilMs(a.dato, Math.max(a.slut, a.start + 15)))}`,
      `SUMMARY:${icsTekst(a.titel)}`, `LOCATION:${icsTekst(a.adresse)}`,
      ...(a.beskrivelse ? [`DESCRIPTION:${icsTekst(a.beskrivelse)}`] : []),
      `ORGANIZER;CN="${navn}":mailto:${i.arrangoer}`,
      ...(i.deltager ? [`ATTENDEE;ROLE=REQ-PARTICIPANT;PARTSTAT=ACCEPTED;RSVP=FALSE:mailto:${i.deltager}`] : []),
      `STATUS:${i.metode === 'CANCEL' ? 'CANCELLED' : 'CONFIRMED'}`, 'TRANSP:OPAQUE', 'END:VEVENT');
  }
  l.push('END:VCALENDAR');
  return l.map(foldLinje).join('\r\n') + '\r\n';
}

/** Filnavn og indholdstype til mailens vedhæftning. */
export const kalenderFil = (metode: KalenderMetode) => ({ filnavn: metode === 'CANCEL' ? 'aflysning.ics' : 'invitation.ics', type: `text/calendar; charset=utf-8; method=${metode}` });

/** Skal mailen have en kalenderfil? Kundens mail med "Vedhæft kalender" (REQUEST – også ved flyt, så aftalen opdateres)
 *  og kundens mail om en aflyst booking (CANCEL – aftalen fjernes). Kun bookinger med dato og tid. */
export function kalenderMetodeFor(w: Pick<Workflow, 'haendelse'>, h: Pick<Handling, 'type' | 'til' | 'kalender'>, k: Pick<Kontekst, 'dato' | 'start' | 'status'>): KalenderMetode | null {
  if (h.type !== 'mail' || h.til !== 'kunde' || !k.dato || k.start == null) return null;
  if (w.haendelse === 'booking_aflyst') return 'CANCEL';
  return h.kalender && k.status !== 'aflyst' ? 'REQUEST' : null;
}

/** Kalenderfilen til en besked (én aftale pr. bil). */
export function kalenderForBesked(metode: KalenderMetode, k: Kontekst, o: { klient: string; arrangoer: string; nu: number }): string {
  const biler = k.biler?.length ? k.biler : [{ booking_id: k.booking_id, ydelse: k.ydelse, bil: k.bil, nummerplade: k.nummerplade, dato: k.dato, start: k.start, slut: k.slut }];
  const adresse = [k.adresse, [k.postnr, k.by].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return ics({
    metode, klient: o.klient, firma: k.firma, arrangoer: o.arrangoer, deltager: k.kunde_email || undefined, nu: o.nu,
    aftaler: biler.filter((b) => b.dato && b.start != null).map((b) => ({
      booking_id: b.booking_id, dato: b.dato, start: b.start!, slut: b.slut ?? b.start! + 60, titel: `${b.ydelse} – ${k.firma}`, adresse,
      beskrivelse: [b.bil && `Bil: ${b.bil}${b.nummerplade ? ` (${b.nummerplade})` : ''}`, `Bookingnr.: ${b.booking_id}`, k.firma_telefon && `Spørgsmål? Ring ${k.firma_telefon}`].filter(Boolean).join('\n'),
    })),
  });
}
